import { useState, useEffect } from 'react'
import API from '../../services/apiService'
import { downloadInstaller, showDownloadError, showDownloadSuccess } from '../../services/downloadService'
import './HeroDownloadButton.css'

const HeroDownloadButton = () => {
  const [botInfo, setBotInfo] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    const fetchBotInfo = async () => {
      try {
        const res = await API.Bot.getLatest()
        if (res.success) {
          setBotInfo(res.data)
        }
      } catch (err) {
        console.error('Failed to fetch bot info:', err)
      }
    }
    fetchBotInfo()
  }, [])

  const handleDownload = async (e) => {
    e.preventDefault()
    
    if (isDownloading) return
    
    setIsDownloading(true)
    
    const result = await downloadInstaller()
    
    if (result.success) {
      showDownloadSuccess(result.message)
    } else {
      showDownloadError(result.error)
    }
    
    setIsDownloading(false)
  }

  const version = botInfo?.version || ''
  const sizeFormatted = botInfo?.installerSizeFormatted || ''
  const changelogRaw = botInfo?.changelog

  let changelogText = ''
  if (!changelogRaw) {
    changelogText = 'No changelog available.'
  } else if (typeof changelogRaw === 'string') {
    changelogText = changelogRaw
  } else if (Array.isArray(changelogRaw)) {
    changelogText = changelogRaw.join('\n')
  } else if (typeof changelogRaw === 'object') {
    try {
      changelogText = JSON.stringify(changelogRaw, null, 2)
    } catch (err) {
      changelogText = String(changelogRaw)
    }
  } else {
    changelogText = String(changelogRaw)
  }

  const changelogFormatted = changelogText
    .split('\n')
    .map(line => `  ${line}`)
    .join('\n')

  const tooltipText = `Version: ${version}\n\nChangelog:\n\n${changelogFormatted}`

  return (
    <div className="hero__actions">
      <div className="hero__btn-wrapper">
        <button 
          onClick={handleDownload}
          disabled={isDownloading}
          className="hero__btn hero__btn--primary"
          aria-label={`Download Unity-BOT ${version}`}
        >
          <div className="hero__btn-shield">
          <svg viewBox="0 0 64 64" fill="none" width="48" height="48">
            <defs>
              {/* Gradientes ornamentados */}
              <linearGradient id="shieldGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffd700"/>
                <stop offset="30%" stopColor="#ffed4e"/>
                <stop offset="50%" stopColor="#ffd700"/>
                <stop offset="70%" stopColor="#d4a227"/>
                <stop offset="100%" stopColor="#8b6914"/>
              </linearGradient>
              <linearGradient id="shieldInner" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#5a4a3a"/>
                <stop offset="50%" stopColor="#3a2a1a"/>
                <stop offset="100%" stopColor="#2a1a10"/>
              </linearGradient>
              <linearGradient id="arrowGreen" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#ffd700"/>
                <stop offset="50%" stopColor="#fbbf24"/>
                <stop offset="100%" stopColor="#c9a227"/>
              </linearGradient>
              <linearGradient id="arrowGold" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#c9a227"/>
                <stop offset="50%" stopColor="#ffd700"/>
                <stop offset="100%" stopColor="#c9a227"/>
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <radialGradient id="gemGlow">
                <stop offset="0%" stopColor="#ffd700" stopOpacity="0.8"/>
                <stop offset="50%" stopColor="#fbbf24" stopOpacity="0.5"/>
                <stop offset="100%" stopColor="#c9a227" stopOpacity="0"/>
              </radialGradient>
            </defs>
            
            {/* Brilho externo do escudo */}
            <path d="M32 4 L56 12 L56 32 C56 48 32 60 32 60 C32 60 8 48 8 32 L8 12 Z" 
              fill="url(#gemGlow)" opacity="0.3"/>
            
            {/* Shield shape - moldura dourada ornamentada */}
            <path d="M32 4 L56 12 L56 32 C56 48 32 60 32 60 C32 60 8 48 8 32 L8 12 Z" 
              fill="url(#shieldGold)" stroke="#6b5110" strokeWidth="2.5"/>
            
            {/* Borda interna brilhante */}
            <path d="M32 6 L54 13 L54 32 C54 47 32 58 32 58 C32 58 10 47 10 32 L10 13 Z" 
              fill="none" stroke="#ffed4e" strokeWidth="0.8" opacity="0.6"/>
            
            {/* Inner shield - fundo escuro */}
            <path d="M32 10 L50 16 L50 32 C50 44 32 54 32 54 C32 54 14 44 14 32 L14 16 Z" 
              fill="url(#shieldInner)"/>
            
            {/* Ornamentos laterais */}
            <g opacity="0.7">
              <circle cx="14" cy="18" r="1.5" fill="#ffd700"/>
              <circle cx="50" cy="18" r="1.5" fill="#ffd700"/>
              <path d="M14 16 L14 20 M12 18 L16 18" stroke="#c9a227" strokeWidth="0.8" strokeLinecap="round"/>
              <path d="M50 16 L50 20 M48 18 L52 18" stroke="#c9a227" strokeWidth="0.8" strokeLinecap="round"/>
            </g>
            
            {/* Seta de download - haste ornamentada */}
            <g filter="url(#glow)">
              {/* Haste principal com gradiente */}
              <path d="M32 20 L32 38" stroke="url(#arrowGold)" strokeWidth="4.5" strokeLinecap="round"/>
              
              {/* Detalhes dourados na haste */}
              <circle cx="32" cy="22" r="1.5" fill="url(#arrowGold)"/>
              <circle cx="32" cy="28" r="1.2" fill="url(#arrowGold)"/>
              <circle cx="32" cy="34" r="1" fill="url(#arrowGold)"/>
              
              {/* Ponta da seta - ornamentada */}
              <path d="M24 32 L32 42 L40 32" stroke="url(#arrowGold)" strokeWidth="4.5" 
                    fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              
              {/* Detalhes dourados na ponta */}
              <path d="M24 32 L32 42 L40 32" stroke="url(#arrowGold)" strokeWidth="1.2" 
                    fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
              
              {/* Gema no topo da seta */}
              <circle cx="32" cy="20" r="2.5" fill="url(#arrowGold)"/>
              <circle cx="32" cy="20" r="1.5" fill="#ffd700" opacity="0.8"/>
              
              {/* Gema na base da seta */}
              <circle cx="32" cy="42" r="2" fill="url(#arrowGold)"/>
              <circle cx="32" cy="42" r="1.2" fill="#ffd700"/>
            </g>
            
            {/* Linhas decorativas verticais */}
            <g opacity="0.4">
              <path d="M20 22 L20 38" stroke="#c9a227" strokeWidth="1.2" strokeLinecap="round"/>
              <path d="M44 22 L44 38" stroke="#c9a227" strokeWidth="1.2" strokeLinecap="round"/>
              
              {/* Pequenos detalhes */}
              <circle cx="20" cy="22" r="0.8" fill="#ffd700"/>
              <circle cx="44" cy="22" r="0.8" fill="#ffd700"/>
              <circle cx="20" cy="38" r="0.8" fill="#ffd700"/>
              <circle cx="44" cy="38" r="0.8" fill="#ffd700"/>
            </g>
            
            {/* Ornamentos no topo do escudo */}
            <g opacity="0.5">
              <path d="M28 12 L32 8 L36 12" stroke="#ffd700" strokeWidth="1.5" 
                    fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="32" cy="8" r="1.2" fill="#ffd700"/>
            </g>
            
            {/* Cruz decorativa no fundo */}
            <g opacity="0.15">
              <path d="M32 15 L32 45" stroke="#c9a227" strokeWidth="3"/>
              <path d="M18 30 L46 30" stroke="#c9a227" strokeWidth="3"/>
            </g>
          </svg>
        </div>
        <div className="hero__btn-text">
          <span className="hero__btn-title">Download Unity-BOT</span>
          <span className="hero__btn-version">
            {version 
              ? `Version ${version}${sizeFormatted ? ` • ${sizeFormatted}` : ''}` 
              : 'Download Now'}
          </span>
        </div>
        <div className="hero__btn-decoration">
          <svg viewBox="0 0 40 40" width="42" height="42" fill="none">
            <defs>
              {/* Gradientes para baú medieval */}
              <linearGradient id="chestWood" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#92400e"/>
                <stop offset="50%" stopColor="#78350f"/>
                <stop offset="100%" stopColor="#451a03"/>
              </linearGradient>
              <linearGradient id="chestWoodDark" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#78350f"/>
                <stop offset="50%" stopColor="#451a03"/>
                <stop offset="100%" stopColor="#1c0a00"/>
              </linearGradient>
              <linearGradient id="chestMetal" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#713f12"/>
                <stop offset="50%" stopColor="#a16207"/>
                <stop offset="100%" stopColor="#713f12"/>
              </linearGradient>
              <linearGradient id="chestGold" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#fbbf24"/>
                <stop offset="50%" stopColor="#f59e0b"/>
                <stop offset="100%" stopColor="#d97706"/>
              </linearGradient>
              <radialGradient id="chestGlow">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.8"/>
                <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.4"/>
                <stop offset="100%" stopColor="#d97706" stopOpacity="0"/>
              </radialGradient>
              <linearGradient id="chestGemRed" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#dc2626"/>
                <stop offset="50%" stopColor="#b91c1c"/>
                <stop offset="100%" stopColor="#7f1d1d"/>
              </linearGradient>
              <linearGradient id="chestGemBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6"/>
                <stop offset="50%" stopColor="#2563eb"/>
                <stop offset="100%" stopColor="#1e40af"/>
              </linearGradient>
            </defs>
            
            {/* Brilho dourado saindo do baú */}
            <ellipse cx="20" cy="12" rx="12" ry="8" fill="url(#chestGlow)" opacity="0.6"/>
            
            <g>
              {/* Base do baú (parte inferior) */}
              <rect x="8" y="20" width="24" height="15" rx="1.5" 
                    fill="url(#chestWood)" stroke="#451a03" strokeWidth="0.5"/>
              
              {/* Tábuas de madeira na base */}
              <g opacity="0.3">
                <line x1="8" y1="23" x2="32" y2="23" stroke="#451a03" strokeWidth="0.4"/>
                <line x1="8" y1="26" x2="32" y2="26" stroke="#451a03" strokeWidth="0.4"/>
                <line x1="8" y1="29" x2="32" y2="29" stroke="#451a03" strokeWidth="0.4"/>
                <line x1="8" y1="32" x2="32" y2="32" stroke="#451a03" strokeWidth="0.4"/>
              </g>
              
              {/* Ferragens metálicas na base */}
              <rect x="7.5" y="24" width="25" height="1.5" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              <rect x="7.5" y="30" width="25" height="1.5" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              
              {/* Cantos reforçados */}
              <circle cx="9" cy="21.5" r="1" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              <circle cx="31" cy="21.5" r="1" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              <circle cx="9" cy="33.5" r="1" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              <circle cx="31" cy="33.5" r="1" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              
              {/* Tampa do baú (aberta) */}
              <path d="M8 20 L8 10 Q8 8 10 8 L30 8 Q32 8 32 10 L32 20" 
                    fill="url(#chestWoodDark)" stroke="#451a03" strokeWidth="0.5"/>
              
              {/* Tábuas na tampa */}
              <g opacity="0.3">
                <line x1="8" y1="11" x2="32" y2="11" stroke="#1c0a00" strokeWidth="0.4"/>
                <line x1="8" y1="14" x2="32" y2="14" stroke="#1c0a00" strokeWidth="0.4"/>
                <line x1="8" y1="17" x2="32" y2="17" stroke="#1c0a00" strokeWidth="0.4"/>
              </g>
              
              {/* Ferragens na tampa */}
              <rect x="7.5" y="12" width="25" height="1.5" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              <rect x="7.5" y="17" width="25" height="1.5" fill="url(#chestMetal)" stroke="#713f12" strokeWidth="0.3"/>
              
              {/* Fechadura ornamentada */}
              <g>
                {/* Base da fechadura */}
                <rect x="17" y="22" width="6" height="5" rx="0.8" 
                      fill="url(#chestGold)" stroke="#92400e" strokeWidth="0.4"/>
                
                {/* Buraco da fechadura */}
                <circle cx="20" cy="24.5" r="1" fill="#451a03"/>
                <path d="M20 25.5 L20 26.5 L19.5 27 L20.5 27 Z" fill="#451a03"/>
                
                {/* Ornamentos */}
                <circle cx="18" cy="23" r="0.4" fill="#fbbf24"/>
                <circle cx="22" cy="23" r="0.4" fill="#fbbf24"/>
                <circle cx="18" cy="26" r="0.4" fill="#fbbf24"/>
                <circle cx="22" cy="26" r="0.4" fill="#fbbf24"/>
              </g>
              
              {/* Brilho dourado escapando */}
              <g opacity="0.8">
                {/* Raios de luz */}
                <path d="M20 19 L20 5" stroke="#fbbf24" strokeWidth="1.5" opacity="0.6"/>
                <path d="M15 18 L12 8" stroke="#f59e0b" strokeWidth="1.2" opacity="0.5"/>
                <path d="M25 18 L28 8" stroke="#f59e0b" strokeWidth="1.2" opacity="0.5"/>
                <path d="M12 17 L8 10" stroke="#d97706" strokeWidth="1" opacity="0.4"/>
                <path d="M28 17 L32 10" stroke="#d97706" strokeWidth="1" opacity="0.4"/>
              </g>
              
              {/* Moedas douradas saindo */}
              <g>
                <ellipse cx="18" cy="15" rx="1.2" ry="0.8" fill="url(#chestGold)" stroke="#d97706" strokeWidth="0.2"/>
                <ellipse cx="17.8" cy="14.7" rx="0.5" ry="0.3" fill="#fef3c7" opacity="0.7"/>
                
                <ellipse cx="22" cy="13" rx="1.2" ry="0.8" fill="url(#chestGold)" stroke="#d97706" strokeWidth="0.2"/>
                <ellipse cx="21.8" cy="12.7" rx="0.5" ry="0.3" fill="#fef3c7" opacity="0.7"/>
                
                <ellipse cx="20" cy="11" rx="1.4" ry="0.9" fill="url(#chestGold)" stroke="#d97706" strokeWidth="0.2"/>
                <ellipse cx="19.8" cy="10.6" rx="0.6" ry="0.4" fill="#fef3c7" opacity="0.7"/>
              </g>
              
              {/* Gemas preciosas saindo */}
              <g>
                {/* Rubi */}
                <ellipse cx="16" cy="12" rx="0.9" ry="1.1" fill="url(#chestGemRed)" stroke="#7f1d1d" strokeWidth="0.2"/>
                <ellipse cx="15.7" cy="11.5" rx="0.4" ry="0.5" fill="#fca5a5" opacity="0.7"/>
                
                {/* Safira */}
                <ellipse cx="24" cy="14" rx="0.9" ry="1.1" fill="url(#chestGemBlue)" stroke="#1e40af" strokeWidth="0.2"/>
                <ellipse cx="23.7" cy="13.5" rx="0.4" ry="0.5" fill="#93c5fd" opacity="0.7"/>
              </g>
              
              {/* Detalhes de brilho no metal */}
              <g opacity="0.5">
                <line x1="9" y1="24.5" x2="31" y2="24.5" stroke="#fbbf24" strokeWidth="0.2"/>
                <line x1="9" y1="30.5" x2="31" y2="30.5" stroke="#fbbf24" strokeWidth="0.2"/>
              </g>
            </g>
          </svg>
        </div>
        </button>
        <div className="hero__tooltip">
          <pre>{tooltipText}</pre>
        </div>
      </div>
    </div>
  )
}

export default HeroDownloadButton
